import { InventoryListing } from "@workspace/api-client-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { BankNumbersPopover } from "@/components/bank-numbers";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import {
  FveStatusPill,
  RowActionsCell,
  PriceCell,
  NoteCell,
  ListingCell,
  TicketsCell,
  ticketGroupBreakdown,
  QrCoverageCell,
  LinkCoverageCell,
  seatSummary,
  SeatPill,
  holdState,
  formatHoldDate,
  formatHoldCountdown,
} from "@/components/inventory-cells";
import type { FveTransitions } from "@/lib/use-fve-transitions";
import { cn } from "@/lib/utils";

// A single label/value pair in a card's field grid. Skipped entirely when the
// value is null so empty cells (e.g. an absent listing ref) don't leave a
// dangling label behind.
function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: ReactNode;
  full?: boolean;
}) {
  if (children == null || children === false) return null;
  return (
    <div className={cn("min-w-0", full && "col-span-2")}>
      <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/70">
        {label}
      </div>
      <div className="mt-0.5 min-w-0 text-sm text-foreground">{children}</div>
    </div>
  );
}

// Compact hold-expiry line shown under the status pill so operators can see a
// hold's timing without opening the pill. Mirrors the colour cues used in the
// desktop hold chip.
function HoldExpiryLine({ row }: { row: InventoryListing }) {
  const hs = holdState(row);
  if (hs === "none" || !row.adminHoldExpiration) return null;
  const when = formatHoldDate(row.adminHoldExpiration);
  const countdown = formatHoldCountdown(row.adminHoldExpiration);
  if (!when) return null;
  return (
    <div
      className={cn(
        "text-[11px] font-mono",
        hs === "expired"
          ? "text-red-400"
          : hs === "expiring"
            ? "text-orange-400"
            : hs === "failed"
              ? "text-red-500"
              : "text-amber-500/90",
      )}
    >
      Hold {hs === "expired" ? "expired" : "expires"} {when}
      {countdown ? ` · ${countdown}` : ""}
    </div>
  );
}

function BankField({ row }: { row: InventoryListing }) {
  if (!row.bankMappingStatus) {
    return (
      <Badge
        variant="outline"
        className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase"
      >
        No Bank
      </Badge>
    );
  }
  return (
    <BankNumbersPopover
      details={{
        label: row.bankCardName || row.bankName || row.bankSource || "Bank",
        variant: row.bankVariant,
        routingNumber: row.bankRouting,
        accountNumber: row.bankAccount,
        routingLast4: row.bankRoutingLast4,
        accountLast4: row.bankAccountLast4,
        variantId: row.bankVariantId,
        mappingStatus: row.bankMappingStatus,
        accountEmail: row.vendorAccountEmail,
      }}
    />
  );
}

export interface InventoryMobileCardProps {
  row: InventoryListing;
  fve: FveTransitions;
  /** Buyback-display mode — swaps the status pill's marketplace tiles for intent. */
  hideGetIn?: boolean;
  defaultHoldDays: number;
  isBusy: boolean;
  onHold: (days: number) => void;
  onUnhold: () => void;
  isAdmin: boolean;
  canAcknowledge: boolean;
  onSavePrice: (price: number) => Promise<unknown>;
  onAcknowledge: () => Promise<unknown>;
  /** Extra labelled fields rendered before phone/bank (event, account, puller). */
  extraFields?: { label: string; node: ReactNode; full?: boolean }[];
  /** Selection checkbox — only rendered when both are supplied. */
  selected?: boolean;
  onToggleSelect?: () => void;
  /** Note editor — only rendered when supplied. */
  onSaveNote?: (note: string | null) => Promise<unknown>;
  showListing?: boolean;
  showSales?: boolean;
  showPhone?: boolean;
  showBank?: boolean;
  showQr?: boolean;
  showLink?: boolean;
}

// A single inventory listing rendered as a readable stacked card for phone-width
// screens. Reuses every interactive desktop cell (status pill, actions, price,
// note) so behaviour is identical — only the layout changes from a wide grid row
// to a vertical card.
export function InventoryMobileCard({
  row,
  fve,
  hideGetIn = false,
  defaultHoldDays,
  isBusy,
  onHold,
  onUnhold,
  isAdmin,
  canAcknowledge,
  onSavePrice,
  onAcknowledge,
  extraFields,
  selected,
  onToggleSelect,
  onSaveNote,
  showListing,
  showSales,
  showPhone,
  showBank,
  showQr,
  showLink,
}: InventoryMobileCardProps) {
  const selectable = onToggleSelect != null;
  const fveListed = !!row.listedOnFve;
  const hasListing = showListing && !!row.listedListingRef;
  const hasTickets = showSales && ticketGroupBreakdown(row).total > 0;
  // An event delivers either QR (international) or transfer links (domestic), so
  // show only the field matching the row's event delivery type. A row whose
  // marketType is unknown keeps the legacy "show whichever has assets" behaviour.
  const isInternational = row.marketType === "international";
  const isDomestic = row.marketType === "domestic";
  const hasQr =
    showQr && !isDomestic && (row.ticketAssets?.some((a) => a.hasPdf) ?? false);
  const hasLink =
    showLink &&
    !isInternational &&
    (row.linkAssets?.some((a) => a.hasLink) ?? false);

  return (
    <div
      className={cn(
        "group/row rounded-md border p-3 transition-colors",
        selected
          ? "border-primary/40 bg-primary/5"
          : fveListed
            ? "border-emerald-500/30 bg-emerald-500/[0.06]"
            : "border-border bg-card",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {selectable && (
            <Checkbox
              checked={!!selected}
              onCheckedChange={onToggleSelect}
              aria-label="Select listing"
              className="mt-0.5 shrink-0 opacity-0 group-hover/row:opacity-100 data-[state=checked]:opacity-100 data-[state=indeterminate]:opacity-100 transition-opacity"
            />
          )}
          <div className="min-w-0 space-y-1">
            <FveStatusPill row={row} fve={fve} hideGetIn={hideGetIn} />
            <HoldExpiryLine row={row} />
          </div>
        </div>
        <RowActionsCell
          row={row}
          defaultHoldDays={defaultHoldDays}
          isBusy={isBusy}
          onHold={onHold}
          onUnhold={onUnhold}
          fve={fve}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2.5">
        {extraFields?.map((f) => (
          <Field key={f.label} label={f.label} full={f.full}>
            {f.node}
          </Field>
        ))}
        <Field label="Seats">
          <SeatPill seat={seatSummary(row)} />
        </Field>
        <Field label="Qty">
          <span className="font-mono text-xs">
            {row.buybackCandidate ? (row.ticketCount ?? 0) : (row.availableQuantity ?? 0)}
          </span>
        </Field>
        <Field label="Price">
          <PriceCell
            row={row}
            isAdmin={isAdmin}
            canAcknowledge={canAcknowledge}
            onSave={onSavePrice}
            onAcknowledge={onAcknowledge}
          />
        </Field>
        {hasListing && (
          <Field label="Listing">
            <ListingCell row={row} />
          </Field>
        )}
        {hasTickets && (
          <Field label="Tickets">
            <TicketsCell listings={[row]} />
          </Field>
        )}
        {hasQr && (
          <Field label="QR">
            <QrCoverageCell row={row} />
          </Field>
        )}
        {hasLink && (
          <Field label="Links">
            <LinkCoverageCell row={row} />
          </Field>
        )}
        {showPhone && (
          <Field label="Phone">
            <PhoneStatusIcon
              status={row.phoneStatus as "good" | "bad" | "unknown" | null}
              lastSmsTime={row.lastSmsTime}
              phoneType={row.phoneType}
              phone={row.phoneOnFile}
            />
          </Field>
        )}
        {showBank && (
          <Field label="Bank">
            <BankField row={row} />
          </Field>
        )}
      </div>

      {onSaveNote && (
        <div className="mt-3 border-t border-border/60 pt-2">
          <NoteCell row={row} onSave={onSaveNote} />
        </div>
      )}
    </div>
  );
}
